'use client'

import { useState, useEffect, useRef, useMemo } from 'react'
import { createClient } from '~/lib/supabase/client'
import Image from 'next/image'
import Link from 'next/link'
import { resolveImageUrl } from '~/lib/images'
import { Users, User, Loader2, MapPin, ChevronDown, X, Search as SearchIcon } from 'lucide-react'
import { env } from '~/env'
import { VisitorEmptyState } from '~/components/auth/VisitorEmptyState'
import { AuthPromptDialog } from '~/components/auth/AuthPromptDialog'

type Member = {
  user_id: string
  full_name: string
  avatar_url: string | null
  pronouns: string | null
  bio: string | null
  member_since: string
  state: string | null // abbreviated, e.g. "VIC"
}

// --- Searchable Filter Component (mirrors the Facilitators directory) ---
function SearchableFilter({
  label,
  placeholder,
  value,
  onChange,
  options,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
  options: string[]
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    if (!query) return options
    const lower = query.toLowerCase()
    return options.filter((o) => o.toLowerCase().includes(lower))
  }, [query, options])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = (option: string) => {
    onChange(option)
    setQuery('')
    setIsOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setQuery('')
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-neutral-emphasis mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          placeholder={value || placeholder}
          value={isOpen ? query : ''}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          className={`w-full px-3 py-2 pr-16 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE866D] focus:border-transparent text-sm ${value && !isOpen ? 'text-neutral-emphasis' : ''}`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value && (
            <button type="button" onClick={handleClear} className="p-0.5 rounded hover:bg-gray-100">
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-border rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
          ) : (
            filtered.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleSelect(option)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#FCEEE8] transition-colors ${value === option ? 'bg-[#FCEEE8] font-medium text-[#DE866D]' : 'text-neutral-emphasis'}`}
              >
                {option}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

// --- Member Name Filter (with avatars, mirroring the facilitator name filter) ---
function MemberNameFilter({
  value,
  onChange,
  members,
}: {
  value: string
  onChange: (value: string) => void
  members: Member[]
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    if (!query) return members
    const lower = query.toLowerCase()
    return members.filter((m) => m.full_name.toLowerCase().includes(lower))
  }, [query, members])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelect = (name: string) => {
    onChange(name)
    setQuery('')
    setIsOpen(false)
  }

  const handleClear = () => {
    onChange('')
    setQuery('')
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-neutral-emphasis mb-2">
        Name
      </label>
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder={value || 'Search members...'}
          value={isOpen ? query : ''}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!isOpen) setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          className={`w-full pl-10 pr-16 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DE866D] focus:border-transparent text-sm ${value && !isOpen ? 'text-neutral-emphasis' : ''}`}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value && (
            <button type="button" onClick={handleClear} className="p-0.5 rounded hover:bg-gray-100">
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          )}
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-border rounded-lg shadow-lg">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
          ) : (
            filtered.map((m) => (
              <button
                key={m.user_id}
                type="button"
                onClick={() => handleSelect(m.full_name)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left ${value === m.full_name ? 'bg-[#FCEEE8]' : ''}`}
              >
                {m.avatar_url ? (
                  <div className="relative w-10 h-10 flex-shrink-0">
                    <Image
                      src={resolveImageUrl(m.avatar_url)}
                      alt={m.full_name}
                      fill
                      className="rounded-full object-cover"
                      unoptimized
                    />
                  </div>
                ) : (
                  <div className="w-10 h-10 bg-neutral-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-black font-medium text-sm">
                      {m.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className={`text-sm font-medium ${value === m.full_name ? 'text-[#DE866D]' : 'text-black'}`}>
                  {m.full_name}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default function FriendsDirectoryPage() {
  const [members, setMembers] = useState<Member[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isGuest, setIsGuest] = useState(false)
  const [showAuthPrompt, setShowAuthPrompt] = useState(false)

  // Filter states
  const [nameFilter, setNameFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')

  const stateOptions = useMemo(() => {
    const states = new Set<string>()
    members.forEach((m) => {
      if (m.state) states.add(m.state)
    })
    return Array.from(states).sort()
  }, [members])

  useEffect(() => {
    async function fetchMembers() {
      try {
        setIsLoading(true)
        setError(null)

        const supabase = createClient()
        const { data: sessionData } = await supabase.auth.getSession()
        const accessToken = sessionData?.session?.access_token

        // Visitors see the page structure (filters) but no member data
        if (!accessToken) {
          setIsGuest(true)
          setShowAuthPrompt(true)
          setIsLoading(false)
          return
        }

        const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/user/members`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          cache: 'no-store',
        })

        if (res.ok) {
          setMembers(await res.json())
        } else {
          console.error('Failed to load members:', res.status)
          setError('Failed to load members. Please try again later.')
        }
      } catch (err) {
        console.error('Error fetching members:', err)
        setError('Failed to load members. Please try again later.')
      } finally {
        setIsLoading(false)
      }
    }

    void fetchMembers()
  }, [])

  const filteredMembers = members.filter((member) => {
    if (nameFilter && member.full_name !== nameFilter) return false
    if (stateFilter && member.state !== stateFilter) return false
    return true
  })

  return (
    <div className="min-h-screen bg-background">
      <AuthPromptDialog
        open={showAuthPrompt}
        onOpenChange={setShowAuthPrompt}
        title="Find your friends in the community"
        description="Create a free account to see which members are part of Path Collective and connect with them."
        icon="🤝"
      />
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="fixed bg-[#F4ECE1]/70 backdrop-blur-md z-25 top-15 pt-2 pb-2 sm:pt-0 sm:top-0 sm:relative flex items-center justify-between mb-6 w-full">
            <div className="p-3">
              <h1 className="text-3xl font-bold text-neutral-emphasis mb-2 flex items-center gap-3">
                <Users className="h-8 w-8 text-[#DE866D]" />
                Friends
              </h1>
              <p className="text-neutral">
                Browse community members, see their profiles, and add them as friends.
              </p>
            </div>
          </div>
        </div>

        <div className="pt-31 sm:pt-0 max-w-7xl mx-auto space-y-10">
          {/* Filter Section */}
          <div className="bg-white rounded-xl border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-neutral-emphasis mb-4">Filters</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <MemberNameFilter value={nameFilter} onChange={setNameFilter} members={members} />
              <SearchableFilter
                label="Location"
                placeholder="All states"
                value={stateFilter}
                onChange={setStateFilter}
                options={stateOptions}
              />
            </div>

            {!isGuest && (
              <div className="mt-4 text-sm text-neutral">
                Showing {filteredMembers.length} of {members.length} members
              </div>
            )}
          </div>

          {/* Visitor State */}
          {!isLoading && isGuest && (
            <VisitorEmptyState
              title="Find your friends in the community"
              description="Create a free account to see which members are part of Path Collective and connect with them."
            />
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#DE866D]" />
              <span className="ml-3 text-neutral">Loading members...</span>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="bg-white rounded-xl border border-border p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-red-300 mb-4" />
              <h3 className="text-lg font-semibold text-neutral-emphasis mb-2">
                Error Loading Members
              </h3>
              <p className="text-neutral">{error}</p>
            </div>
          )}

          {/* Members Grid */}
          {!isLoading && !error && filteredMembers.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMembers.map((member) => (
                <Link
                  key={member.user_id}
                  href={`/member-profile/${member.user_id}`}
                  className="bg-white rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group"
                >
                  <div className="p-6">
                    {/* Avatar */}
                    <div className="flex flex-col items-center mb-4">
                      <div className="relative flex-shrink-0 mb-3">
                        {member.avatar_url ? (
                          <div className="relative w-20 h-20 rounded-full overflow-hidden">
                            <Image
                              src={resolveImageUrl(member.avatar_url)}
                              alt={member.full_name}
                              fill
                              className="object-cover"
                              unoptimized
                            />
                          </div>
                        ) : (
                          <div className="w-20 h-20 bg-[#FCEEE8] rounded-full flex items-center justify-center">
                            <User className="h-10 w-10 text-[#DE866D]" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Name */}
                    <div className="mb-2">
                      <h3 className="font-semibold text-neutral-emphasis text-lg group-hover:text-[#DE866D] transition-colors text-center">
                        {member.full_name}
                      </h3>
                    </div>

                    {/* Pronouns / member badge */}
                    <div className="mb-3">
                      <p className="text-neutral text-sm text-center">
                        {member.pronouns || 'Member'}
                      </p>
                    </div>

                    {/* Location */}
                    {member.state && (
                      <div className="flex items-center gap-2 text-neutral justify-center">
                        <MapPin className="h-4 w-4 flex-shrink-0 text-neutral-500" />
                        <span className="text-sm truncate">{member.state}</span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* No Results After Filtering */}
          {!isLoading && !error && members.length > 0 && filteredMembers.length === 0 && (
            <div className="bg-white rounded-xl border border-border p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-neutral-300 mb-4" />
              <h3 className="text-lg font-semibold text-neutral-emphasis mb-2">
                No Matching Members
              </h3>
              <p className="text-neutral">
                No members match your current filters. Try adjusting your search criteria.
              </p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && !isGuest && members.length === 0 && (
            <div className="bg-white rounded-xl border border-border p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-neutral-300 mb-4" />
              <h3 className="text-lg font-semibold text-neutral-emphasis mb-2">
                No Members Found
              </h3>
              <p className="text-neutral">
                No members have made their profile public yet. Check back later.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
